/**
 * Gap splitting and 3D Douglas-Peucker.
 *
 * Runs are kept SEPARATE rather than merged into a MultiLineString:
 * `featureToAlignment` calls `concatMultiLine`, which would silently weld the
 * geometry across a gap into a bogus alignment (spec §7.3).
 */
import type { CrestParams, CrestPoint, CrestRun } from './types';

export function splitRuns(picks: ReadonlyArray<CrestPoint | null>, params: CrestParams): CrestPoint[][] {
  const out: CrestPoint[][] = [];
  let cur: CrestPoint[] = [];
  const flush = () => {
    // (count - 1) intervals, not count: two points 2 m apart span 2 m.
    if (cur.length >= 2 && (cur.length - 1) * params.stationInterval >= params.minRunLength) out.push(cur);
    cur = [];
  };
  for (const p of picks) {
    if (p === null) flush();
    else cur.push(p);
  }
  flush();
  return out;
}

/**
 * Douglas-Peucker on flattened XYZ, using perpendicular distance to the 3D
 * line through the endpoints. Iterative (explicit stack) so a long run cannot
 * blow the call stack.
 */
export function rdp3d(points: Float64Array, tolerance: number): Float64Array {
  const n = points.length / 3;
  if (n < 3) return points.slice();
  const keep = new Uint8Array(n);
  keep[0] = 1;
  keep[n - 1] = 1;
  const tol2 = tolerance * tolerance;
  const stack: Array<[number, number]> = [[0, n - 1]];
  while (stack.length > 0) {
    const [i0, i1] = stack.pop()!;
    if (i1 - i0 < 2) continue;
    const ax = points[i0 * 3];
    const ay = points[i0 * 3 + 1];
    const az = points[i0 * 3 + 2];
    const dx = points[i1 * 3] - ax;
    const dy = points[i1 * 3 + 1] - ay;
    const dz = points[i1 * 3 + 2] - az;
    const l2 = dx * dx + dy * dy + dz * dz;
    let best = -1;
    let bestD2 = -1;
    for (let i = i0 + 1; i < i1; i++) {
      const px = points[i * 3] - ax;
      const py = points[i * 3 + 1] - ay;
      const pz = points[i * 3 + 2] - az;
      let d2: number;
      if (l2 <= 0) {
        d2 = px * px + py * py + pz * pz;
      } else {
        const t = (px * dx + py * dy + pz * dz) / l2;
        const cx = px - t * dx;
        const cy = py - t * dy;
        const cz = pz - t * dz;
        d2 = cx * cx + cy * cy + cz * cz;
      }
      if (d2 > bestD2) {
        bestD2 = d2;
        best = i;
      }
    }
    if (bestD2 > tol2 && best > 0) {
      keep[best] = 1;
      stack.push([i0, best], [best, i1]);
    }
  }
  let kept = 0;
  for (let i = 0; i < n; i++) kept += keep[i];
  const out = new Float64Array(kept * 3);
  let w = 0;
  for (let i = 0; i < n; i++) {
    if (!keep[i]) continue;
    out[w * 3] = points[i * 3];
    out[w * 3 + 1] = points[i * 3 + 1];
    out[w * 3 + 2] = points[i * 3 + 2];
    w++;
  }
  return out;
}

export function buildRun(pts: ReadonlyArray<CrestPoint>, params: CrestParams): CrestRun {
  const raw = new Float64Array(pts.length * 3);
  for (let i = 0; i < pts.length; i++) {
    raw[i * 3] = pts[i].x;
    raw[i * 3 + 1] = pts[i].y;
    raw[i * 3 + 2] = pts[i].z;
  }
  const points = rdp3d(raw, params.simplifyTolerance);
  let lengthM = 0;
  for (let i = 1; i < points.length / 3; i++) {
    lengthM += Math.hypot(
      points[i * 3] - points[(i - 1) * 3],
      points[i * 3 + 1] - points[(i - 1) * 3 + 1],
      points[i * 3 + 2] - points[(i - 1) * 3 + 2],
    );
  }
  return { points, lengthM, startStation: pts[0].station };
}
